import { useEffect, useRef } from "react";
import { useTraining } from "../contexts/TrainingContext";
import {
  getTipsByPage,
  getTipsByComponent,
  getTipById,
} from "../config/trainingTips";

export const useTrainingTips = (page, component = null) => {
  const { isTrainingEnabled, dismissedTips, showTip, closeTip } = useTraining();
  const hasShownPageTips = useRef(false);

  // Show tips for a specific component when it mounts
  const showTipForComponent = (
    tipId,
    targetElement = null,
    position = "bottom",
    delay = 500,
    onDismissCallback = null
  ) => {
    if (!isTrainingEnabled || dismissedTips.has(tipId)) {
      return;
    }

    const tip = getTipById(tipId);
    if (tip) {
      setTimeout(() => {
        showTip({
          ...tip,
          targetElement,
          position,
          onDismissCallback, // Pass the callback to the tip
        });
      }, delay);
    }
  };

  // Show the next available tip for a page
  const showNextPageTip = (targetElement = null, position = "center") => {
    if (!isTrainingEnabled || hasShownPageTips.current) {
      return;
    }

    const pageTips = getTipsByPage(page);
    const availableTip = pageTips.find((tip) => !dismissedTips.has(tip.id));

    if (availableTip) {
      hasShownPageTips.current = true;
      setTimeout(() => {
        showTip({
          ...availableTip,
          targetElement,
          position,
        });
      }, 1000);
    }
  };

  // Show tips for a specific component
  const showComponentTips = (
    targetComponent,
    targetElement = null,
    position = "bottom"
  ) => {
    if (!isTrainingEnabled) {
      return;
    }

    const componentTips = getTipsByComponent(page, targetComponent);
    const availableTip = componentTips.find(
      (tip) => !dismissedTips.has(tip.id)
    );

    if (availableTip) {
      setTimeout(() => {
        showTip({
          ...availableTip,
          targetElement,
          position,
        });
      }, 500);
    }
  };

  // Check if a specific tip should be shown
  const shouldShowTip = (tipId) => {
    return isTrainingEnabled && !dismissedTips.has(tipId);
  };

  // Add training indicator to an element
  const addTrainingIndicator = (element, tipId, indicatorType = "default") => {
    if (!element || !isTrainingEnabled || dismissedTips.has(tipId)) {
      return;
    }

    // Remove existing indicator classes
    element.classList.remove(
      "training-indicator",
      "button-indicator",
      "section-indicator",
      "form-section-indicator",
      "subtle"
    );

    // Add base training indicator class
    element.classList.add("training-indicator");

    // Add specific indicator type
    switch (indicatorType) {
      case "button":
        element.classList.add("button-indicator");
        break;
      case "section":
        element.classList.add("section-indicator");
        break;
      case "form-section":
        element.classList.add("form-section-indicator");
        break;
      case "subtle":
        element.classList.add("subtle");
        break;
      default:
        break;
    }
  };

  // Remove training indicator from an element
  const removeTrainingIndicator = (element) => {
    if (!element) return;

    element.classList.remove(
      "training-indicator",
      "button-indicator",
      "section-indicator",
      "form-section-indicator",
      "subtle"
    );
  };

  // Get training indicator class names for an element
  const getTrainingIndicatorClass = (tipId, indicatorType = "default") => {
    if (!isTrainingEnabled || dismissedTips.has(tipId)) {
      return "";
    }

    let classes = "training-indicator";

    switch (indicatorType) {
      case "button":
        classes += " button-indicator";
        break;
      case "section":
        classes += " section-indicator";
        break;
      case "form-section":
        classes += " form-section-indicator";
        break;
      case "subtle":
        classes += " subtle";
        break;
      default:
        break;
    }

    return classes;
  };

  // Reset the page tips flag when training is toggled
  useEffect(() => {
    hasShownPageTips.current = false;
  }, [isTrainingEnabled]);

  return {
    isTrainingEnabled,
    showTipForComponent,
    showNextPageTip,
    showComponentTips,
    shouldShowTip,
    addTrainingIndicator,
    removeTrainingIndicator,
    getTrainingIndicatorClass,
    closeTip,
  };
};
